import {useInfiniteQuery, useQueries, useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  SidebarCountsResponse,
  Timeframe,
  TimeframesService,
  WorkstreamAccess,
  WorkstreamAuditService,
  WorkstreamListService,
  WorkstreamService,
  WorkstreamsResponse,
} from 'client';
import type {ScopeType} from 'client/models/ScopeType';
import {useEffect, useMemo, useRef} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

import {
  LAST_UPDATED_FILTER_DEFAULT_VALUE,
  resolveLastUpdatedFilterValue,
} from './lastUpdatedFilter';
import {
  buildWorkstreamV2QueryPerfSignature,
  getPerfNow,
  toPerfGroupBy,
  toPerfScopeValue,
} from './perf';
import type {
  GroupBy,
  LastUpdatedFilterValue,
  RestrictedWorkstreamSummary,
  SortDirection,
  StatusValue,
  WorkstreamFilters,
} from './types';
import {
  sanitizeLastUpdatedFilters,
  sanitizeMissingInformationFilters,
  sanitizeTimeframeFilters,
  STATUS_SEMANTIC_ORDER,
} from './types';
import {
  getDefaultWorkstreamTimeframe,
  getKnownTimeframes,
  setKnownTimeframes,
  type WorkstreamTimeframe,
} from './workstreamTimeframe';

// ============================================================================
// Query Keys
// ============================================================================

export const workstreamDataKeys = {
  all: ['workstream-data'] as const,
  audit: (onTimeTimeframe?: string | null) =>
    [...workstreamDataKeys.all, 'audit', onTimeTimeframe ?? 'all'] as const,
  sidebar: (timeframe?: string | null) =>
    [...workstreamDataKeys.all, 'sidebar', timeframe ?? 'default'] as const,
  timeframes: () => [...workstreamDataKeys.all, 'timeframes'] as const,
  pillars: (timeframe?: string | null) =>
    [...workstreamDataKeys.all, 'pillars', timeframe ?? 'default'] as const,
  pillarConfigs: (timeframe?: string | null) =>
    [...workstreamDataKeys.all, 'pillar-configs', timeframe ?? 'default'] as const,
  facets: (params: Record<string, unknown>) =>
    [...workstreamDataKeys.all, 'facets', params] as const,
  facetWorkstreamOptions: (params: Record<string, unknown>) =>
    [...workstreamDataKeys.all, 'facet-workstream-options', params] as const,
  facetPeopleOptions: (params: Record<string, unknown>) =>
    [...workstreamDataKeys.all, 'facet-people-options', params] as const,
  facetTeamOptions: (params: Record<string, unknown>) =>
    [...workstreamDataKeys.all, 'facet-team-options', params] as const,
  workstreams: (params: Record<string, unknown>) =>
    [...workstreamDataKeys.all, 'workstreams', params] as const,
};

export const cleanWorkstreamFilters = (
  filters: WorkstreamFilters,
  lastUpdatedDefaultValue: LastUpdatedFilterValue = LAST_UPDATED_FILTER_DEFAULT_VALUE
): Record<string, unknown> => {
  const defaultNonArchivedHealthStatuses = STATUS_SEMANTIC_ORDER.filter(
    (status): status is Exclude<StatusValue, 'archived'> => status !== 'archived'
  );
  const selectedHealthStatuses = Array.isArray(filters.health) ? filters.health : [];
  const includeArchived = selectedHealthStatuses.includes('archived');
  const explicitNonArchivedHealthSelections = defaultNonArchivedHealthStatuses.filter((status) =>
    selectedHealthStatuses.includes(status)
  );
  const selectedLastUpdated = resolveLastUpdatedFilterValue(
    sanitizeLastUpdatedFilters(filters.last_updated as unknown[] | undefined),
    lastUpdatedDefaultValue
  );

  const cleanFilters: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    // Keep frontend-only filters local to UI/state.
    if (key === '_version' || key === 'health' || key === 'altitude' || key === 'last_updated') {
      continue;
    }
    if (Array.isArray(value)) {
      if (key === 'missing_information') {
        const sanitized = sanitizeMissingInformationFilters(value);
        if (sanitized && sanitized.length > 0) {
          cleanFilters[key] = sanitized;
        }
        continue;
      }
      if (key === 'timeframes') {
        const sanitized = sanitizeTimeframeFilters(value);
        if (sanitized && sanitized.length > 0) {
          cleanFilters.timeframes = sanitized;
        }
        continue;
      }
      if (value.length > 0 || key === 'workstreams') {
        cleanFilters[key] = value;
      }
    }
  }

  if (explicitNonArchivedHealthSelections.length > 0) {
    cleanFilters.health = includeArchived
      ? [...explicitNonArchivedHealthSelections, 'archived']
      : explicitNonArchivedHealthSelections;
  } else if (!includeArchived) {
    cleanFilters.health = defaultNonArchivedHealthStatuses;
  }
  cleanFilters.last_updated = [selectedLastUpdated];

  return cleanFilters;
};

const WORKSTREAM_SORT_COLUMN_TO_API: Record<string, string> = {
  title: 'name',
  status: 'health',
  eta: 'status_due_date',
  owner: 'dri',
  last_update: 'status_update_needed',
};
const ALLOWED_WORKSTREAM_API_SORT_COLUMNS = new Set([
  'id',
  'name',
  'pillar',
  'health',
  'status_due_date',
  'status_update_needed',
  'dri',
  'manual',
]);

const mapSortColumnForWorkstreamsApi = (sortColumn?: string | null): string | undefined => {
  if (!sortColumn) return undefined;
  const mappedSortColumn = WORKSTREAM_SORT_COLUMN_TO_API[sortColumn] ?? sortColumn;
  return ALLOWED_WORKSTREAM_API_SORT_COLUMNS.has(mappedSortColumn) ? mappedSortColumn : undefined;
};

// ============================================================================
// Data Queries
// ============================================================================

/**
 * Fetch sidebar counts for navigation (optionally scoped to a planning narrative).
 */
export const useSidebarCounts = (timeframe?: string | null) => {
  return useQuery({
    queryKey: workstreamDataKeys.sidebar(timeframe),
    queryFn: () =>
      getService(WorkstreamListService).getSidebarCountsApiV1WorkstreamSidebarCountsGet(timeframe),
  });
};

/**
 * Fetch the read-only Workstream Audit projection.
 */
export const useWorkstreamAudit = ({
  enabled = true,
  onTimeTimeframe,
}: {
  enabled?: boolean;
  onTimeTimeframe?: string | null;
} = {}) => {
  return useQuery({
    queryKey: workstreamDataKeys.audit(onTimeTimeframe),
    queryFn: () =>
      getService(WorkstreamAuditService).getWorkstreamAuditApiV1WorkstreamAuditGet(
        onTimeTimeframe ?? undefined
      ),
    placeholderData: (previousData) => previousData,
    enabled,
    // Fresh for a minute, not the global 30: opening Audit or coming back to
    // the tab after that refetches, but every mount and focus doesn't. Edits
    // and remote changes invalidate it sooner.
    staleTime: 60 * 1000,
    retry: false,
    throwOnError: false,
  });
};

/**
 * Fetch distinct pillar names for filtering (per planning narrative when provided).
 */
/**
 * Planning timeframes sorted by start, from `GET /api/v1/timeframes`, with the
 * request's state. Until the request succeeds it returns the list this
 * browser loaded last (empty on a first visit), and it keeps the non-React
 * timeframe helpers in sync with the API.
 */
export const useTimeframesQuery = ({enabled = true}: {enabled?: boolean} = {}): {
  timeframes: readonly Timeframe[];
  isLoading: boolean;
  isError: boolean;
} => {
  const {data, isLoading, isError} = useQuery({
    queryKey: workstreamDataKeys.timeframes(),
    enabled,
    queryFn: () => getService(TimeframesService).listTimeframesApiV1TimeframesGet(),
    staleTime: 5 * 60 * 1000,
  });
  // Synced during render, not in an effect, so the render that receives the
  // list (and its children) already sees it in the non-React helpers.
  if (data) {
    setKnownTimeframes(data);
  }
  return {timeframes: getKnownTimeframes(), isLoading, isError};
};

/** Planning timeframes sorted by start. See `useTimeframesQuery`. */
export const useTimeframes = (): readonly Timeframe[] => useTimeframesQuery().timeframes;

/**
 * The default planning timeframe (see `getDefaultWorkstreamTimeframe`),
 * fetching the timeframes if needed. Empty until they load.
 */
export const useDefaultWorkstreamTimeframe = (): WorkstreamTimeframe =>
  getDefaultWorkstreamTimeframe(new Date(), useTimeframes());

export const usePillars = (timeframe?: string | null) => {
  return useQuery({
    queryKey: workstreamDataKeys.pillars(timeframe),
    queryFn: () => getService(WorkstreamListService).getPillarsApiV1WorkstreamPillarsGet(timeframe),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Fetch pillar link configurations for a planning period (defaults to server default).
 */
export const usePillarConfigs = (timeframe?: string | null) => {
  return useQuery({
    queryKey: workstreamDataKeys.pillarConfigs(timeframe),
    queryFn: () =>
      getService(WorkstreamListService).getPillarConfigsApiV1WorkstreamPillarConfigsGet(timeframe),
    staleTime: 5 * 60 * 1000,
  });
};

const WORKSTREAMS_PAGE_SIZE = 100;
const DEFAULT_EAGER_PREFETCH_ROW_CAP = 300;
/** Default list cache freshness; aligns with `QueryClientWrapper` global `staleTime`. Mutations + collab still invalidate. */
const DEFAULT_WORKSTREAMS_LIST_STALE_TIME_MS = 1000 * 60 * 30;
type WorkstreamsQueryPage = WorkstreamsResponse & {
  __cursor: number | null;
};

/**
 * Fetch workstreams with filtering, sorting, and grouping.
 */
export const useWorkstreams = ({
  scope_type,
  scope_value,
  narrative_only,
  filters,
  group_by,
  active_group,
  sort_column,
  sort_direction,
  lastUpdatedDefaultValue = LAST_UPDATED_FILTER_DEFAULT_VALUE,
  autoFetchAllPages = false,
  eagerPrefetchEnabled = false,
  eagerPrefetchRowCap = DEFAULT_EAGER_PREFETCH_ROW_CAP,
  enabled = true,
  trackPerf = false,
  staleTimeMs = DEFAULT_WORKSTREAMS_LIST_STALE_TIME_MS,
}: {
  scope_type: string;
  scope_value?: string | null;
  narrative_only?: boolean;
  filters: WorkstreamFilters;
  group_by?: GroupBy;
  active_group?: string | null;
  sort_column?: string | null;
  sort_direction?: SortDirection;
  lastUpdatedDefaultValue?: LastUpdatedFilterValue;
  autoFetchAllPages?: boolean;
  eagerPrefetchEnabled?: boolean;
  eagerPrefetchRowCap?: number;
  enabled?: boolean;
  trackPerf?: boolean;
  staleTimeMs?: number;
}) => {
  // Clean filters: omit _version and empty arrays, except keep explicit empty
  // workstreams filters (needed for empty-tracker routes).
  const cleanFilters = useMemo(
    () => cleanWorkstreamFilters(filters, lastUpdatedDefaultValue),
    [filters, lastUpdatedDefaultValue]
  );

  const filtersParam =
    Object.keys(cleanFilters).length > 0 ? JSON.stringify(cleanFilters) : undefined;
  const hasEffectiveFilters = Object.keys(cleanFilters).length > 0;
  const requestedWorkstreamIds = Array.isArray(cleanFilters.workstreams)
    ? (cleanFilters.workstreams as number[])
    : null;
  const requestedSortColumn = sort_column ?? undefined;
  const requestedApiSortColumn = mapSortColumnForWorkstreamsApi(sort_column);
  const isManualSort = requestedApiSortColumn === 'manual';
  // For manual sort, pass undefined for group_by — the backend only accepts null,
  // not the string "none", alongside sort_column=manual.
  const requestedGroupBy = isManualSort ? undefined : group_by === 'none' ? 'none' : group_by;
  const requestedActiveGroup =
    requestedGroupBy && requestedGroupBy !== 'none' ? active_group : null;
  const requestedSortDirection = requestedApiSortColumn ? (sort_direction ?? 'asc') : undefined;
  const queryPerfSignature = useMemo(
    () =>
      buildWorkstreamV2QueryPerfSignature({
        activeGroup: requestedActiveGroup,
        scopeType: scope_type,
        scopeValue: scope_value,
        filtersParam,
        groupBy: requestedGroupBy,
        sortColumn: requestedSortColumn,
        sortDirection: requestedSortDirection,
      }),
    [
      scope_type,
      scope_value,
      filtersParam,
      requestedGroupBy,
      requestedActiveGroup,
      requestedSortColumn,
      requestedSortDirection,
    ]
  );
  const queryPerfRef = useRef<{
    signature: string;
    startedAt: number;
    firstPageLogged: boolean;
    completeLogged: boolean;
    errorLogged: boolean;
  }>({
    signature: queryPerfSignature,
    startedAt: getPerfNow(),
    firstPageLogged: false,
    completeLogged: false,
    errorLogged: false,
  });
  const eagerPrefetchPerfRef = useRef<{
    signature: string;
    startedAt: number | null;
    completedAt: number | null;
    pagesRequested: number;
    rowsLoadedBeforeScrollEnd: number;
  }>({
    signature: queryPerfSignature,
    startedAt: null,
    completedAt: null,
    pagesRequested: 0,
    rowsLoadedBeforeScrollEnd: 0,
  });
  const workstreamsQueryKey = useMemo(
    () =>
      workstreamDataKeys.workstreams({
        scope_type,
        scope_value,
        narrative_only,
        filtersParam,
        group_by: requestedGroupBy,
        active_group: requestedActiveGroup,
        sort_column: requestedSortColumn,
        api_sort_column: requestedApiSortColumn,
        sort_direction: requestedSortDirection,
        hasEffectiveFilters,
        requestedWorkstreamIds,
      }),
    [
      scope_type,
      scope_value,
      narrative_only,
      filtersParam,
      requestedGroupBy,
      requestedActiveGroup,
      requestedSortColumn,
      requestedApiSortColumn,
      requestedSortDirection,
      hasEffectiveFilters,
      requestedWorkstreamIds,
    ]
  );

  useEffect(
    () => () => {
      void queryClient.cancelQueries({queryKey: workstreamsQueryKey, exact: true});
    },
    [workstreamsQueryKey]
  );

  const {
    data: paginatedData,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchNextPageError,
    isFetchingNextPage,
    isLoading,
    isPending,
    refetch,
  } = useInfiniteQuery<WorkstreamsQueryPage>({
    queryKey: workstreamsQueryKey,
    queryFn: async ({pageParam, signal}) => {
      const cursor = typeof pageParam === 'number' ? pageParam : undefined;
      const request = getService(WorkstreamListService).getWorkstreamsApiV1WorkstreamWorkstreamsGet(
        scope_type as ScopeType,
        scope_value,
        filtersParam,
        requestedApiSortColumn,
        requestedSortDirection,
        requestedGroupBy,
        requestedActiveGroup,
        narrative_only,
        WORKSTREAMS_PAGE_SIZE,
        cursor
      );
      const abortRequest = () => request.cancel();
      signal?.addEventListener('abort', abortRequest, {once: true});

      try {
        const response = await request;
        return {
          ...response,
          __cursor: cursor ?? null,
        };
      } finally {
        signal?.removeEventListener('abort', abortRequest);
      }
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, allPages) => {
      const requestedCursors = new Set<number>(
        allPages.map((page) => page.__cursor).filter((cursor): cursor is number => cursor != null)
      );
      const nextCursor = lastPage.next_cursor;
      if (nextCursor == null || !lastPage.has_more) {
        return undefined;
      }
      if (requestedCursors.has(nextCursor)) {
        return undefined;
      }
      return nextCursor;
    },
    enabled,
    staleTime: staleTimeMs,
  });

  useEffect(() => {
    if (!trackPerf) return;

    queryPerfRef.current = {
      signature: queryPerfSignature,
      startedAt: getPerfNow(),
      firstPageLogged: false,
      completeLogged: false,
      errorLogged: false,
    };
    eagerPrefetchPerfRef.current = {
      signature: queryPerfSignature,
      startedAt: null,
      completedAt: null,
      pagesRequested: 0,
      rowsLoadedBeforeScrollEnd: 0,
    };

    analyticsLogger().logEvent('WORKSTREAM_V2_PERF_QUERY_START', {
      auto_fetch_all_pages: autoFetchAllPages,
      eager_prefetch_enabled: Boolean(eagerPrefetchEnabled),
      eager_prefetch_row_cap: Math.max(0, Math.floor(eagerPrefetchRowCap)),
      active_group: requestedActiveGroup ?? 'all',
      group_by: toPerfGroupBy(requestedGroupBy),
      has_effective_filters: hasEffectiveFilters,
      query_signature: queryPerfSignature,
      requested_workstream_ids_count: requestedWorkstreamIds?.length ?? 0,
      scope_type,
      scope_value: toPerfScopeValue(scope_value),
      sort_column: requestedSortColumn ?? 'none',
      sort_direction: requestedSortDirection ?? 'none',
    });
  }, [
    trackPerf,
    queryPerfSignature,
    scope_type,
    scope_value,
    requestedGroupBy,
    requestedActiveGroup,
    requestedSortColumn,
    requestedSortDirection,
    hasEffectiveFilters,
    requestedWorkstreamIds?.length,
    autoFetchAllPages,
    eagerPrefetchEnabled,
    eagerPrefetchRowCap,
  ]);

  useEffect(() => {
    if (!trackPerf) return;

    const pages = paginatedData?.pages;
    if (!pages || pages.length === 0) return;

    const perfState = queryPerfRef.current;
    if (perfState.signature !== queryPerfSignature || perfState.firstPageLogged) return;

    perfState.firstPageLogged = true;
    const firstPage = pages[0];

    analyticsLogger().logEvent('WORKSTREAM_V2_PERF_QUERY_FIRST_PAGE', {
      duration_ms: Math.round(getPerfNow() - perfState.startedAt),
      first_page_rows: firstPage.workstreams.length,
      first_page_total_count: firstPage.total_count,
      group_by: toPerfGroupBy(requestedGroupBy ?? firstPage.group_by),
      has_more: Boolean(firstPage.has_more),
      active_group: firstPage.active_group ?? 'all',
      query_signature: queryPerfSignature,
      request_count: pages.length,
      scope_type,
      scope_value: toPerfScopeValue(scope_value),
    });
  }, [
    trackPerf,
    paginatedData?.pages,
    queryPerfSignature,
    scope_type,
    scope_value,
    requestedGroupBy,
  ]);

  useEffect(() => {
    if (!trackPerf) return;

    const pages = paginatedData?.pages;
    if (!pages || pages.length === 0 || hasNextPage || isFetchingNextPage) return;

    const perfState = queryPerfRef.current;
    if (perfState.signature !== queryPerfSignature || perfState.completeLogged) return;

    perfState.completeLogged = true;
    const loadedRows = pages.reduce((sum, page) => sum + page.workstreams.length, 0);
    const loadedRestrictedRows = pages.reduce(
      (sum, page) => sum + (page.restricted_workstreams?.length ?? 0),
      0
    );
    const eagerPerfState = eagerPrefetchPerfRef.current;
    const eagerPrefetchDurationMs =
      eagerPerfState.signature === queryPerfSignature &&
      eagerPerfState.startedAt != null &&
      eagerPerfState.completedAt != null
        ? Math.round(eagerPerfState.completedAt - eagerPerfState.startedAt)
        : null;

    analyticsLogger().logEvent('WORKSTREAM_V2_PERF_QUERY_COMPLETE', {
      duration_ms: Math.round(getPerfNow() - perfState.startedAt),
      eager_prefetch_pages:
        eagerPerfState.signature === queryPerfSignature ? eagerPerfState.pagesRequested : 0,
      rows_loaded_before_scroll_end:
        eagerPerfState.signature === queryPerfSignature
          ? eagerPerfState.rowsLoadedBeforeScrollEnd
          : 0,
      time_to_all_rows_loaded_capped: eagerPrefetchDurationMs ?? undefined,
      group_by: toPerfGroupBy(requestedGroupBy ?? pages[0]?.group_by),
      has_effective_filters: hasEffectiveFilters,
      loaded_restricted_rows: loadedRestrictedRows,
      loaded_rows: loadedRows,
      page_count: pages.length,
      query_signature: queryPerfSignature,
      reported_total_count: pages[0]?.total_count ?? 0,
      request_count: pages.length,
      scope_type,
      scope_value: toPerfScopeValue(scope_value),
    });
  }, [
    trackPerf,
    paginatedData?.pages,
    hasNextPage,
    isFetchingNextPage,
    queryPerfSignature,
    scope_type,
    scope_value,
    requestedGroupBy,
    hasEffectiveFilters,
  ]);

  useEffect(() => {
    if (!trackPerf || !isError) return;

    const perfState = queryPerfRef.current;
    if (perfState.signature !== queryPerfSignature || perfState.errorLogged) return;

    perfState.errorLogged = true;
    const errorMessage =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'unknown';

    analyticsLogger().logEvent('WORKSTREAM_V2_PERF_QUERY_ERROR', {
      duration_ms: Math.round(getPerfNow() - perfState.startedAt),
      error_message: errorMessage,
      active_group: requestedActiveGroup ?? 'all',
      group_by: toPerfGroupBy(requestedGroupBy),
      query_signature: queryPerfSignature,
      scope_type,
      scope_value: toPerfScopeValue(scope_value),
    });
  }, [
    trackPerf,
    isError,
    error,
    queryPerfSignature,
    scope_type,
    scope_value,
    requestedGroupBy,
    requestedActiveGroup,
  ]);

  const shouldAutoFetchAllPages = Boolean(autoFetchAllPages);
  const shouldEagerPrefetch = Boolean(eagerPrefetchEnabled);
  const normalizedEagerPrefetchRowCap = Math.max(0, Math.floor(eagerPrefetchRowCap));

  useEffect(() => {
    if (!shouldEagerPrefetch) return;

    const pages = paginatedData?.pages;
    if (!pages || pages.length === 0) return;

    const eagerPerfState = eagerPrefetchPerfRef.current;
    if (eagerPerfState.signature !== queryPerfSignature) {
      eagerPrefetchPerfRef.current = {
        signature: queryPerfSignature,
        startedAt: null,
        completedAt: null,
        pagesRequested: 0,
        rowsLoadedBeforeScrollEnd: 0,
      };
      return;
    }

    if (eagerPerfState.startedAt == null) {
      eagerPerfState.startedAt = getPerfNow();
    }

    const loadedRows = pages.reduce(
      (sum, page) => sum + page.workstreams.length + (page.restricted_workstreams?.length ?? 0),
      0
    );
    const reachedCap =
      normalizedEagerPrefetchRowCap > 0 && loadedRows >= normalizedEagerPrefetchRowCap;

    if ((reachedCap || !hasNextPage) && eagerPerfState.completedAt == null) {
      eagerPerfState.completedAt = getPerfNow();
      eagerPerfState.rowsLoadedBeforeScrollEnd = loadedRows;
    }

    if (reachedCap || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return;

    eagerPerfState.pagesRequested += 1;
    void fetchNextPage();
  }, [
    shouldEagerPrefetch,
    normalizedEagerPrefetchRowCap,
    paginatedData?.pages,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    queryPerfSignature,
  ]);

  useEffect(() => {
    if (!shouldAutoFetchAllPages || !hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [shouldAutoFetchAllPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const mergedData = useMemo(() => {
    const pages = paginatedData?.pages;
    if (!pages || pages.length === 0) {
      return undefined;
    }

    const firstPage = pages[0];
    const firstResponse: WorkstreamsResponse = {
      workstreams: firstPage.workstreams,
      groups: firstPage.groups,
      group_by: firstPage.group_by,
      active_group: firstPage.active_group ?? null,
      total_count: firstPage.total_count,
      hidden_count: firstPage.hidden_count,
      scope_counts: firstPage.scope_counts,
      restricted_workstreams: firstPage.restricted_workstreams,
      next_cursor: firstPage.next_cursor,
      has_more: firstPage.has_more,
    };
    const mergedWorkstreams: WorkstreamAccess[] = [];
    const seenWorkstreamIds = new Set<number>();
    for (const page of pages) {
      for (const workstream of page.workstreams) {
        if (seenWorkstreamIds.has(workstream.id)) {
          continue;
        }
        seenWorkstreamIds.add(workstream.id);
        mergedWorkstreams.push(workstream as WorkstreamAccess);
      }
    }

    const mergedRestricted: RestrictedWorkstreamSummary[] = [];
    const seenRestrictedIds = new Set<number>();
    for (const page of pages) {
      for (const restricted of page.restricted_workstreams ?? []) {
        if (seenRestrictedIds.has(restricted.id)) {
          continue;
        }
        seenRestrictedIds.add(restricted.id);
        mergedRestricted.push(restricted);
      }
    }

    const hasLoadedAllPages = !hasNextPage;

    return {
      ...firstResponse,
      groups: firstResponse.groups,
      workstreams: mergedWorkstreams,
      restricted_workstreams: mergedRestricted,
      has_more: Boolean(hasNextPage),
      next_cursor: hasNextPage ? (pages[pages.length - 1]?.next_cursor ?? null) : null,
      total_count:
        hasEffectiveFilters && hasLoadedAllPages
          ? mergedWorkstreams.length + mergedRestricted.length
          : firstResponse.total_count,
      hidden_count:
        hasEffectiveFilters && hasLoadedAllPages
          ? mergedRestricted.length
          : firstResponse.hidden_count,
    };
  }, [paginatedData?.pages, hasNextPage, hasEffectiveFilters]);
  const hasFirstPageForCurrentSignature = Boolean((paginatedData?.pages?.length ?? 0) > 0);

  return {
    data: mergedData,
    error,
    fetchNextPage,
    hasNextPage,
    hasFirstPageForCurrentSignature,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPending,
    querySignature: queryPerfSignature,
    refetch,
  };
};

/**
 * Fetch a full workstream detail payload (including dependencies) by ID.
 * Used by inline subworkstream expansion in v2 table.
 */
const workstreamDetailQueryOptions = (workstreamId?: number) => ({
  queryKey: [...workstreamDataKeys.all, 'workstream-detail', workstreamId],
  queryFn: () => getService(WorkstreamService).getApiV1WorkstreamIdGet(workstreamId!),
  enabled: workstreamId !== undefined,
  refetchOnWindowFocus: 'always' as const,
  staleTime: 60 * 1000,
});

export const useWorkstreamDetail = (workstreamId?: number) => {
  const query = useQuery(workstreamDetailQueryOptions(workstreamId));

  return query;
};

/** Same queries as `useWorkstreamDetail`, for a list of workstreams at once. */
export const useWorkstreamDetails = (workstreamIds: number[]) =>
  useQueries({queries: workstreamIds.map((id) => workstreamDetailQueryOptions(id))});

// ============================================================================
// Re-exports for convenience
// ============================================================================

export type {SidebarCountsResponse};
